import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
  Checkbox,
  ListItemText,
  OutlinedInput,
  SelectChangeEvent,
  Box,
  Tooltip,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Upload as UploadIcon,
  Forum as ForumIcon,
  PlaylistAdd as SaveCaseIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useTestsAccess } from '../services/testsApi';
import { SaveTestCaseDialog, TesterSnapshot, testerSnapshot } from './tests/SaveTestCaseDialog';
import { isVideoModel as checkIsVideoModel, getModelOptionsForAllowedModels } from '../config/modelConfig';
import { ModelSelector } from './ModelSelector';
import { REQUEST_HEADERS } from '../constants/headers';
import {
  prepareFileForSubmission,
  prepareTextForSubmission,
  prepareDataURLForSubmission,
  buildAPIRequestBody,
} from '../utils/fileHandler';

interface TestMaterial {
  id: string;
  name: string;
  description?: string;
  data: {
    content_type: string;
    content?: string;
    file_url?: string;
    file_key?: string;
    file_name?: string;
    file_size?: number;
    metadata?: any;
  };
}

interface FunctionTesterProps {
  functionId?: string;
  functionName: string;
  customerTag: string;
  inputTypes?: string[];
  allowedModels?: string[];
  testMaterials?: TestMaterial[];
  onTestComplete?: (result: any) => void;
  functionMetadata?: any;
}

export const FunctionTester: React.FC<FunctionTesterProps> = ({
  functionId,
  functionName,
  customerTag,
  inputTypes = ['text'],
  allowedModels = ['gpt-4o-mini'],
  testMaterials = [],
  onTestComplete,
  functionMetadata,
}) => {
  const { user, getIdToken } = useAuth();
  const { api: testsApi, enabled: testsEnabled } = useTestsAccess(getIdToken);
  const [caseSnapshot, setCaseSnapshot] = useState<TesterSnapshot | null>(null);
  const [testInput, setTestInput] = useState('');
  const [testAdditionalInstructions, setTestAdditionalInstructions] = useState('');
  const [testFile, setTestFile] = useState<File | null>(null);
  const [testInputType, setTestInputType] = useState<string>(inputTypes[0] || 'text');
  const [selectedModel, setSelectedModel] = useState<string>(allowedModels[0] || 'gpt-4o-mini');
  const [selectedTestMaterial, setSelectedTestMaterial] = useState<TestMaterial | null>(null);
  const [testResult, setTestResult] = useState<any>(null);
  const [testLoading, setTestLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [threadMode, setThreadMode] = useState(false);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [threadHistory, setThreadHistory] = useState<any[]>([]);
  const [inputTokensHeader, setInputTokensHeader] = useState<string | null>(null);
  const [outputTokensHeader, setOutputTokensHeader] = useState<string | null>(null);
  const [creditsUsedHeader, setCreditsUsedHeader] = useState<string | null>(null);
  const [totalCreditsUsed, setTotalCreditsUsed] = useState<number>(0);
  const [vectorSearchEnabled, setVectorSearchEnabled] = useState(false);
  const [categories, setCategories] = useState<Array<{id: string, name: string}>>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [topK, setTopK] = useState(8);
  const [chatHistoryLength, setChatHistoryLength] = useState(10); // Default thread history length
  
  // Video generation settings
  const [videoDuration, setVideoDuration] = useState(6); // 4-8 seconds for video models (default 6 for better quality)
  const [videoAspectRatio, setVideoAspectRatio] = useState('16:9');
  
  // Check if selected model is a video generation model
  const isVideoModel = selectedModel ? checkIsVideoModel(selectedModel) : false;

  // Reset credit counters when switching functions or customer context
  useEffect(() => {
    setCreditsUsedHeader(null);
    setTotalCreditsUsed(0);
  }, [functionName, customerTag]);

  // Detect chatbot mode from metadata and auto-enable thread mode, set history length
  useEffect(() => {
    if (functionMetadata?.chatbot === true || functionMetadata?.chatbot === 'true') {
      setThreadMode(true);
      // Set chat history length from metadata, default to 30 if not specified
      if (functionMetadata?.chat_history_length) {
        setChatHistoryLength(functionMetadata.chat_history_length);
      } else {
        setChatHistoryLength(30);
      }
    }
  }, [functionMetadata?.chatbot, functionMetadata?.chat_history_length]);

  // Fetch categories on mount and auto-select knowledge categories from metadata
  useEffect(() => {
    const fetchCategories = async () => {
      setCategoriesLoading(true);
      try {
        const idToken = await user?.getIdToken();
        const response = await fetch(`/api/v2/customers/${customerTag}/categories`, {
          headers: {
            'Authorization': `Bearer ${idToken}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          const fetchedCategories = data.data?.items || [];
          setCategories(fetchedCategories);

          // Auto-select knowledge categories from metadata if present
          if (functionMetadata?.knowledge_categories && Array.isArray(functionMetadata.knowledge_categories)) {
            const validCategoryIds = functionMetadata.knowledge_categories.filter((catId: string) =>
              fetchedCategories.some((cat: any) => cat.id === catId)
            );
            if (validCategoryIds.length > 0) {
              setSelectedCategories(validCategoryIds);
              setVectorSearchEnabled(true);
            }
          }
        } else {

        }
      } catch (error) {

      } finally {
        setCategoriesLoading(false);
      }
    };
    
    if (customerTag && user) {
      fetchCategories();
    }
  }, [customerTag, user, functionMetadata?.knowledge_categories]);

  const handleCategoryChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setSelectedCategories(typeof value === 'string' ? value.split(',') : value);
  };

  const handleTestFunction = async () => {
    if (!functionName || (!testInput && !testFile)) {
      setError('Please provide test input');
      return;
    }

    setTestLoading(true);
    setTestResult(null);
    setError(null);

    try {
      const idToken = await user?.getIdToken();
      
      let requestBody: any;
      
      // Use the unified file handler
      if (testFile) {
        // File upload - use prepareFileForSubmission (with token for GCS upload)
        const prepared = await prepareFileForSubmission(testFile, testInput || undefined, idToken);
        requestBody = buildAPIRequestBody(prepared, selectedModel);
      } else if (['image', 'video', 'audio', 'pdf', 'file'].includes(testInputType) && testInput && testInput.startsWith('data:')) {
        // Base64 data URL from test material or previous upload
        const prepared = prepareDataURLForSubmission(
          testInput,
          'test-file',
          testAdditionalInstructions || undefined
        );
        requestBody = buildAPIRequestBody(prepared, selectedModel);
      } else {
        // Text or JSON input
        const prepared = prepareTextForSubmission(
          testInput,
          testInputType as 'text' | 'json'
        );
        requestBody = buildAPIRequestBody(prepared, selectedModel);
      }
      
      // Add video generation metadata if using a video model
      if (isVideoModel) {
        if (!requestBody.metadata) requestBody.metadata = {};
        requestBody.metadata.duration = videoDuration;
        requestBody.metadata.aspect_ratio = videoAspectRatio;
      }
      
      // Add vector search metadata if enabled
      if (vectorSearchEnabled && selectedCategories.length > 0) {
        if (!requestBody.metadata) requestBody.metadata = {};
        requestBody.metadata.vector_search = {
          enabled: true,
          category_ids: selectedCategories,
          top_k: topK
        };
      }
      
      const headers: any = { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      };
      
      // Add thread headers if in thread mode
      if (threadMode) {
        // Generate thread ID if not exists
        if (!threadId) {
          const newThreadId = `thread-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
          setThreadId(newThreadId);
          headers['EASYAI-THREAD-ID'] = newThreadId;
        } else {
          headers['EASYAI-THREAD-ID'] = threadId;
        }
        headers['EASYAI-MAX-THREAD-LEN'] = chatHistoryLength.toString(); // Use chatHistoryLength
      }
      

      
      const response = await fetch(`/api/v2/functions/${customerTag}/${functionName}`, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody),
      });

      const inputTokensHeader = response.headers.get(REQUEST_HEADERS.INPUT_TOKENS);
      if (inputTokensHeader) {
        setInputTokensHeader(inputTokensHeader);
      }
      
      const outputTokensHeader = response.headers.get(REQUEST_HEADERS.OUTPUT_TOKENS);
      if (outputTokensHeader) {
        setOutputTokensHeader(outputTokensHeader);
      }
      
      const creditsUsedHeader = response.headers.get(REQUEST_HEADERS.CREDITS_USED);
      if (creditsUsedHeader) {
        setCreditsUsedHeader(creditsUsedHeader);
        const creditsValue = Number(creditsUsedHeader);
        if (!Number.isNaN(creditsValue)) {
          setTotalCreditsUsed(prev => prev + creditsValue);
        }
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.detail || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setTestResult(data);
      
      // Add to thread history if in thread mode
      if (threadMode) {
        let inputDescription = testInput || 'File upload';
        if (testInputType === 'image' && testInput && testInput.startsWith('data:') && testAdditionalInstructions) {
          inputDescription = `Image + "${testAdditionalInstructions}"`;
        }
        setThreadHistory(prev => [...prev, {
          input: inputDescription,
          output: data,
          timestamp: new Date().toISOString()
        }]);
      }
      
      if (onTestComplete) {
        onTestComplete(data);
      }
    } catch (error) {

      setError(error instanceof Error ? error.message : 'Failed to test function');
    } finally {
      setTestLoading(false);
    }
  };

  const handleSelectTestMaterial = async (materialId: string) => {
    const material = testMaterials.find(m => m.id === materialId);
    if (material) {
      setSelectedTestMaterial(material);
      setTestInputType(material.data.content_type || 'text');
      
      if (material.data.content_type === 'text' || material.data.content_type === 'json') {
        setTestInput(material.data.content || '');
        setTestFile(null);
      } else if (['image', 'audio', 'video', 'pdf', 'file'].includes(material.data.content_type)) {
        // For file types
        if (material.data.content && material.data.content.startsWith('data:')) {
          // Base64 encoded file - we'll send it directly as input
          setTestInput(material.data.content);
          setTestFile(null);
          setTestAdditionalInstructions(''); // Clear any previous additional instructions
        } else if (material.data.file_url) {
          // File stored in GCS - fetch via proxy endpoint to avoid CORS issues
          try {
            setTestLoading(true);

            
            const idToken = await user?.getIdToken();
            const response = await fetch(`/api/v2/test-materials/${material.id}/file`, {
              headers: {
                'Authorization': `Bearer ${idToken}`
              }
            });
            
            if (!response.ok) {
              throw new Error(`Failed to fetch file: ${response.statusText}`);
            }
            
            const blob = await response.blob();
            const reader = new FileReader();
            
            reader.onloadend = () => {
              const base64 = reader.result as string;

              setTestInput(base64);
              setTestFile(null);
              setTestAdditionalInstructions(''); // Clear any previous additional instructions
            };
            
            reader.readAsDataURL(blob);
          } catch (error) {

            setTestInput(`Error loading file: ${error instanceof Error ? error.message : 'Unknown error'}`);
            setTestFile(null);
          } finally {
            setTestLoading(false);
          }
        } else {
          // Fallback - show info
          setTestInput(`File: ${material.data.file_name || 'Uploaded file'}\nType: ${material.data.content_type}\nSize: ${material.data.file_size ? (material.data.file_size / 1024 / 1024).toFixed(2) + ' MB' : 'Unknown'}`);
          setTestFile(null);
        }
      }
    }
  };

  const handleCopyResult = () => {
    navigator.clipboard.writeText(JSON.stringify(testResult, null, 2));
  };

  const handleSaveAsCase = async () => {
    if (!functionId) return;
    try {
      setCaseSnapshot(await testerSnapshot({
        functionId,
        functionName,
        material: selectedTestMaterial,
        file: testFile,
        input: testInput,
        inputType: testInputType,
        additionalInstructions: testAdditionalInstructions,
        output: testResult?.data?.results ?? testResult?.data ?? testResult,
      }));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not read the input');
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setTestFile(file);
      // Don't clear text input - allow both file and text
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        {/* Test Materials Dropdown */}
        {testMaterials.length > 0 && (
          <Paper variant="outlined" sx={{ p: 3, mb: 3, backgroundColor: 'rgba(255,255,255,0.9)' }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500 }}>
              Quick Start with Test Materials
            </Typography>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Select Test Material</InputLabel>
              <Select
                value={selectedTestMaterial?.id || ''}
                label="Select Test Material"
                onChange={(e) => handleSelectTestMaterial(e.target.value)}
              >
                {testMaterials.map((material) => (
                  <MenuItem key={material.id} value={material.id}>{material.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Paper>
        )}
        
        {/* Input Type and Model Selection */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {inputTypes.length > 1 && (
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Input Type</InputLabel>
                <Select
                  value={testInputType}
                  onChange={(e) => {
                    setTestInputType(e.target.value);
                    setTestFile(null);
                    setTestAdditionalInstructions('');
                  }}
                  label="Input Type"
                >
                  {inputTypes.map(type => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          
          {allowedModels.length > 1 && (
            <Grid item xs={inputTypes.length > 1 ? 6 : 12}>
              <ModelSelector
                value={selectedModel}
                onChange={(val) => setSelectedModel(val as string)}
                options={getModelOptionsForAllowedModels(allowedModels)}
                label="Model"
              />
            </Grid>
          )}
        </Grid>
        
        {/* Video Generation Settings */}
        {isVideoModel && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2, backgroundColor: 'rgba(255, 243, 224, 0.5)' }}>
            <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              🎥 Video Generation Settings
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Duration (seconds)</InputLabel>
                  <Select
                    value={videoDuration}
                    onChange={(e) => setVideoDuration(Number(e.target.value))}
                    label="Duration (seconds)"
                  >
                    <MenuItem value={4}>4 seconds</MenuItem>
                    <MenuItem value={5}>5 seconds</MenuItem>
                    <MenuItem value={6}>6 seconds</MenuItem>
                    <MenuItem value={7}>7 seconds</MenuItem>
                    <MenuItem value={8}>8 seconds</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Aspect Ratio</InputLabel>
                  <Select
                    value={videoAspectRatio}
                    onChange={(e) => setVideoAspectRatio(e.target.value)}
                    label="Aspect Ratio"
                  >
                    <MenuItem value="16:9">16:9 (Landscape)</MenuItem>
                    <MenuItem value="9:16">9:16 (Portrait)</MenuItem>
                    <MenuItem value="1:1">1:1 (Square)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
              Veo supports 4-8 second videos. Generation may take 1-5 minutes.
            </Typography>
          </Paper>
        )}
        
        {/* Thread Mode Control */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2, backgroundColor: 'rgba(240, 248, 255, 0.5)' }}>
          <FormControlLabel
            control={
              <Switch
                checked={threadMode}
                onChange={(e) => {
                  setThreadMode(e.target.checked);
                  if (!e.target.checked) {
                    // Reset thread when turning off
                    setThreadId(null);
                    setThreadHistory([]);
                  }
                }}
                color="primary"
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ForumIcon fontSize="small" />
                <Typography variant="body2">
                  Thread Mode - Maintain conversation context
                </Typography>
              </Box>
            }
          />
          {threadMode && (
            <Box sx={{ mt: 2, ml: 4, display: 'flex', gap: 2, alignItems: 'center' }}>
              <TextField
                label="Conversation Memory Length"
                type="number"
                value={chatHistoryLength}
                onChange={(e) => setChatHistoryLength(parseInt(e.target.value) || 30)}
                size="small"
                sx={{ width: 180 }}
                inputProps={{ min: 1, max: 100 }}
                helperText="Messages to retain in conversation"
              />
            </Box>
          )}
          {threadMode && threadId && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="textSecondary">
                Thread ID: {threadId}
              </Typography>
              {threadHistory.length > 0 && (
                <Chip 
                  label={`${threadHistory.length} messages in thread`}
                  size="small"
                  sx={{ ml: 2 }}
                />
              )}
            </Box>
          )}
        </Paper>
        
        {/* Vector Search Control */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2, backgroundColor: 'rgba(255, 248, 240, 0.5)' }}>
          <FormControlLabel
            control={
              <Switch
                checked={vectorSearchEnabled}
                onChange={(e) => {
                  setVectorSearchEnabled(e.target.checked);
                  if (!e.target.checked) {
                    setSelectedCategories([]);
                  }
                }}
                color="primary"
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2">
                  Enable Vector Search - Augment with relevant embeddings
                </Typography>
              </Box>
            }
          />
          {vectorSearchEnabled && (
            <Box sx={{ mt: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel id="category-select-label">Select Categories</InputLabel>
                <Select
                  labelId="category-select-label"
                  multiple
                  value={selectedCategories}
                  onChange={handleCategoryChange}
                  input={<OutlinedInput label="Select Categories" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip 
                          key={value} 
                          label={categories.find(c => c.id === value)?.name || value}
                          size="small"
                        />
                      ))}
                    </Box>
                  )}
                  disabled={categoriesLoading}
                >
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      <Checkbox checked={selectedCategories.includes(category.id)} />
                      <ListItemText primary={category.name} />
                    </MenuItem>
                  ))}
                  {categories.length === 0 && !categoriesLoading && (
                    <MenuItem disabled>
                      <Typography variant="body2" color="textSecondary">
                        No categories available
                      </Typography>
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
              {selectedCategories.length > 0 && (
                <>
                  <TextField
                    type="number"
                    label="Top K Results"
                    value={topK}
                    onChange={(e) => {
                      const value = parseInt(e.target.value);
                      if (value > 0 && value <= 20) {
                        setTopK(value);
                      }
                    }}
                    size="small"
                    sx={{ mt: 2, width: 150 }}
                    inputProps={{ min: 1, max: 20 }}
                    helperText="1-20 chunks"
                  />
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
                    Will search top {topK} relevant chunks from selected categories
                  </Typography>
                </>
              )}
            </Box>
          )}
        </Paper>
        
        {/* Test Input */}
        {testInputType === 'text' || testInputType === 'json' ? (
          <TextField
            fullWidth
            multiline
            rows={8}
            label="Test Input"
            placeholder={testInputType === 'json' ? '{\n  "key": "value"\n}' : 'Enter your test input here...'}
            value={testInput}
            onChange={(e) => setTestInput(e.target.value)}
            sx={{ mb: 2 }}
          />
        ) : (
          <Box sx={{ mb: 2 }}>
            <input
              accept={testInputType === 'image' ? 'image/*' : 
                      testInputType === 'video' ? 'video/*' : 
                      testInputType === 'audio' ? 'audio/*' : 
                      testInputType === 'pdf' ? 'application/pdf' : 
                      testInputType === 'json' ? 'application/json' : '*/*'}
              style={{ display: 'none' }}
              id="test-file-upload"
              type="file"
              onChange={handleFileUpload}
            />
            <label htmlFor="test-file-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<UploadIcon />}
                fullWidth
              >
                Upload {testInputType} File
              </Button>
            </label>
            {testFile && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" color="textSecondary">
                  Selected: {testFile.name} ({(testFile.size / 1024).toFixed(2)} KB)
                </Typography>
              </Box>
            )}
            
            {/* Show preview for media files if we have base64 data */}
            {testInputType === 'image' && testInput && testInput.startsWith('data:image') && (
              <Box sx={{ mt: 2, mb: 2 }}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Image Preview:
                </Typography>
                <Box 
                  component="img" 
                  src={testInput} 
                  alt="Test image"
                  sx={{ 
                    maxWidth: '100%', 
                    maxHeight: 200, 
                    border: '1px solid #ddd',
                    borderRadius: 1,
                    display: 'block'
                  }}
                />
              </Box>
            )}
            {testInputType === 'video' && testInput && testInput.startsWith('data:video') && (
              <Box sx={{ mt: 2, mb: 2 }}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Video Preview:
                </Typography>
                <video 
                  src={testInput} 
                  controls
                  style={{ 
                    maxWidth: '100%', 
                    maxHeight: 200, 
                    border: '1px solid #ddd',
                    borderRadius: 4,
                    display: 'block'
                  }}
                />
              </Box>
            )}
            {testInputType === 'audio' && testInput && testInput.startsWith('data:audio') && (
              <Box sx={{ mt: 2, mb: 2 }}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Audio Preview:
                </Typography>
                <audio 
                  src={testInput} 
                  controls
                  style={{ 
                    width: '100%',
                    display: 'block'
                  }}
                />
              </Box>
            )}
            
            {/* Additional text input for context/instructions */}
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Additional Instructions (optional)"
              placeholder="Add any additional context or instructions for the AI..."
              value={['image', 'video', 'audio', 'pdf', 'file'].includes(testInputType) && testInput && testInput.startsWith('data:') ? testAdditionalInstructions : testInput}
              onChange={(e) => {
                if (['image', 'video', 'audio', 'pdf', 'file'].includes(testInputType) && testInput && testInput.startsWith('data:')) {
                  setTestAdditionalInstructions(e.target.value);
                } else {
                  setTestInput(e.target.value);
                }
              }}
              sx={{ mt: 2 }}
            />
          </Box>
        )}
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Tooltip 
          title={!functionName ? "Please save the function first" : !testInput && !testFile ? "Please provide test input" : ""}
          disableHoverListener={!(!functionName || (!testInput && !testFile))}
        >
          <span style={{ width: '100%' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleTestFunction}
              disabled={!functionName || (!testInput && !testFile) || testLoading}
              sx={{ mt: 2 }}
              fullWidth
              startIcon={testLoading ? <CircularProgress size={20} /> : null}
            >
              {testLoading ? 'Testing...' : 'Run Test'}
            </Button>
          </span>
        </Tooltip>
      </Grid>
      
      <Grid item xs={12} md={6}>
        {/* Test Result */}
        <Paper variant="outlined" sx={{ p: 3, backgroundColor: 'rgba(255,255,255,0.9)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500 }}>Response</Typography>
            <Box>
              {testsEnabled && functionId && testResult && !testLoading && !threadMode && (
                <Tooltip title="Save as test case">
                  <IconButton size="small" onClick={handleSaveAsCase}>
                    <SaveCaseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Copy result">
                <IconButton size="small" onClick={handleCopyResult}>
                  <CopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          {testLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 120 }}>
              <CircularProgress size={24} />
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : testResult ? (
            <>
              <Paper sx={{ p: 2, mb: 2, fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                {(() => {
                  // Check if this is a video generation response
                  if (testResult && typeof testResult === 'object' && 'video_url' in testResult) {
                    const videoUrl = testResult.video_url || testResult.url || testResult.gcs_url || testResult.public_url;
                    return (
                      <Box sx={{ fontFamily: 'inherit' }}>
                        <Typography variant="h6" gutterBottom>Generated Video:</Typography>
                        {videoUrl && (
                          <Box sx={{ maxWidth: '100%' }}>
                            <video 
                              src={videoUrl} 
                              controls
                              style={{ 
                                maxWidth: '100%', 
                                maxHeight: '512px',
                                borderRadius: '8px', 
                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)' 
                              }}
                            />
                            <Box sx={{ mt: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                              {testResult.gcs_url && (
                                <Typography variant="caption" color="success.main" sx={{ display: 'block', mb: 0.5 }}>
                                  ✓ Stored in GCS (24hr retention)
                                </Typography>
                              )}
                              {testResult.duration && (
                                <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                                  Duration: {testResult.duration}s
                                </Typography>
                              )}
                              <Typography 
                                variant="caption" 
                                color="primary" 
                                sx={{ display: 'block', mt: 0.5, cursor: 'pointer', textDecoration: 'underline' }}
                                onClick={() => {
                                  navigator.clipboard.writeText(videoUrl);
                                  alert('Video URL copied to clipboard!');
                                }}
                              >
                                📋 Copy Video URL
                              </Typography>
                            </Box>
                          </Box>
                        )}
                        {testResult.metadata && (
                          <Alert severity="info" sx={{ mt: 2 }}>
                            <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
                              {JSON.stringify(testResult.metadata, null, 2)}
                            </Typography>
                          </Alert>
                        )}
                      </Box>
                    );
                  }
                  
                  // Check if this is an image generation response
                  if (testResult && typeof testResult === 'object' && 'images' in testResult) {
                    const images = testResult.images;
                    if (Array.isArray(images) && images.length > 0) {
                      return (
                        <Box sx={{ fontFamily: 'inherit' }}>
                          <Typography variant="h6" gutterBottom>Generated Images ({images.length}):</Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                            {images.map((img: any, idx: number) => (
                              <Box key={idx} sx={{ maxWidth: '100%' }}>
                                {/* Show image from URL or GCS URL */}
                                {(img.url || img.gcs_url || img.public_url) && (
                                  <Box>
                                    <img 
                                      src={img.url || img.public_url || img.gcs_url} 
                                      alt={`Generated output ${idx + 1}`}
                                      style={{ 
                                        maxWidth: '100%', 
                                        maxHeight: '512px',
                                        height: 'auto', 
                                        borderRadius: '8px', 
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                        cursor: 'pointer'
                                      }}
                                      onClick={() => window.open(img.url || img.public_url, '_blank')}
                                      title="Click to open full size"
                                    />
                                    <Box sx={{ mt: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                                      {img.revised_prompt && (
                                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 0.5 }}>
                                          <strong>Revised:</strong> {img.revised_prompt}
                                        </Typography>
                                      )}
                                      {img.gcs_url && (
                                        <Typography variant="caption" color="success.main" sx={{ display: 'block', mb: 0.5 }}>
                                          ✓ Stored in GCS (24hr retention)
                                        </Typography>
                                      )}
                                      {img.size_bytes && (
                                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                                          Size: {(img.size_bytes / 1024).toFixed(1)}KB
                                        </Typography>
                                      )}
                                      <Typography 
                                        variant="caption" 
                                        color="primary" 
                                        sx={{ display: 'block', mt: 0.5, cursor: 'pointer', textDecoration: 'underline' }}
                                        onClick={() => {
                                          navigator.clipboard.writeText(img.url || img.public_url);
                                          alert('URL copied to clipboard!');
                                        }}
                                      >
                                        📋 Copy URL
                                      </Typography>
                                    </Box>
                                  </Box>
                                )}
                                {/* Show base64 image */}
                                {img.data && img.data.startsWith('data:image') && (
                                  <Box>
                                    <img 
                                      src={img.data} 
                                      alt={`Generated output ${idx + 1}`}
                                      style={{ maxWidth: '100%', height: 'auto', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
                                    />
                                  </Box>
                                )}
                              </Box>
                            ))}
                          </Box>
                          {testResult.metadata && (
                            <Alert severity="info" sx={{ mt: 2 }}>
                              <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
                                {JSON.stringify(testResult.metadata, null, 2)}
                              </Typography>
                            </Alert>
                          )}
                        </Box>
                      );
                    }
                  }
                  
                  // Check if this is a text response wrapped in the Response object
                  if (testResult && typeof testResult === 'object' && 
                      'response' in testResult && 
                      Object.keys(testResult).length === 1) {
                    const responseText = testResult.response;
                    
                    // If the response value is an object or array, show as formatted JSON
                    if (typeof responseText === 'object' && responseText !== null) {
                      return JSON.stringify(responseText, null, 2);
                    }
                    
                    // Plain text/string response
                    return String(responseText ?? '');
                  }
                  
                  // For all other responses, show as JSON
                  return JSON.stringify(testResult, null, 2);
                })()}
              </Paper>
              {inputTokensHeader && (
                <Typography variant="body2" color="textSecondary">
                  Input tokens: <strong>{Number(inputTokensHeader).toLocaleString()}</strong>
                </Typography>
              )}
              {outputTokensHeader && (
                <Typography variant="body2" color="textSecondary">
                  Output tokens: <strong>{Number(outputTokensHeader).toLocaleString()}</strong>
                </Typography>
              )}
              {creditsUsedHeader && (
                <Typography variant="body2" color="textSecondary">
                  Credits used (last run): <strong>{Number(creditsUsedHeader).toLocaleString()}</strong>
                </Typography>
              )}
              {totalCreditsUsed > 0 && (
                <Typography variant="body2" color="textSecondary">
                  Total credits used (this session): <strong>{totalCreditsUsed.toLocaleString()}</strong>
                </Typography>
              )}
            </>
          ) : (
            <Typography variant="body2" color="textSecondary">No response yet.</Typography>
          )}
        </Paper>
      </Grid>
      
      {/* Thread History */}
      {threadMode && threadHistory.length > 0 && (
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 3, mt: 2, backgroundColor: 'rgba(240, 248, 255, 0.3)' }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ForumIcon /> Thread History
            </Typography>
            <Box sx={{ maxHeight: '300px', overflowY: 'auto' }}>
              {threadHistory.map((entry, index) => (
                <Box key={index} sx={{ mb: 2, pb: 2, borderBottom: index < threadHistory.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}>
                  <Typography variant="caption" color="textSecondary">
                    Message {index + 1} - {new Date(entry.timestamp).toLocaleTimeString()}
                  </Typography>
                  <Box sx={{ mt: 1, display: 'flex', gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" color="primary">Input:</Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>
                        {entry.input}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" color="success.main">Output:</Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>
                        {JSON.stringify(entry.output, null, 2)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setThreadId(null);
                setThreadHistory([]);
              }}
              sx={{ mt: 2 }}
            >
              Clear Thread & Start New
            </Button>
          </Paper>
        </Grid>
      )}
      <SaveTestCaseDialog open={!!caseSnapshot} api={testsApi} snapshot={caseSnapshot} onClose={() => setCaseSnapshot(null)} />
    </Grid>
  );
};
